import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { MenuItem, Checkbox, ListItemText, CircularProgress, Button } from '@mui/material';
import { SelectMenu } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { currencyFormatter } from 'src/utils/translator';
import { isValueNumber } from 'src/utils/typeValidators';
import { COLORS, MEASURE } from './palette';
import { Panel, KpiCard, EmptyState } from './components';
import { pickReceiptReimbursement, pickReceiptWithheld } from './reimbursement';
import { WipBanner } from '../WipBanner';
import useStyles from './styles';
const formatCurrency = value => isValueNumber(value) ? currencyFormatter(value) : 'N/A';

// The "Reimbursement Receipts" tab (Haley's PRD): a payment-driven receipt for a
// single property and period - the money Livable actually COLLECTED from residents
// through the software and owes back to the property. Nothing else lives here.
//
// The charge-recovery analytics (KPI row, recovery-by-property/charge-type charts,
// breakdown table) were the old Metabase report that was flagged as inaccurate and
// hidden; they are intentionally NOT rendered on this tab. The full charge-recovery
// dashboard still lives on the "Dashboard" tab (ReimbursementTabV2) for anyone who
// needs it. See ReimbursementReceiptCalculator for the receipt's include/exclude rules.
export const ReimbursementReceiptsCustomTab = observer(() => {
  var _findReceiptPeriod$pe, _findReceiptPeriod, _receipt$transactions;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isLoading = _useState2[0],
    setIsLoading = _useState2[1];
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    propertyOptions = _useState4[0],
    setPropertyOptions = _useState4[1];

  // Per-period Reimbursement Receipt: payment-driven, single property.
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    receipt = _useState6[0],
    setReceipt = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    receiptPeriods = _useState8[0],
    setReceiptPeriods = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    receiptPeriodKey = _useState0[0],
    setReceiptPeriodKey = _useState0[1]; // 'YYYY-M'
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    receiptLoading = _useState10[0],
    setReceiptLoading = _useState10[1];

  // The receipt is property-and-month scoped, so it only loads when exactly one
  // property is selected above.
  const getFilters = async () => {
    const resp = await store.report.getReimbursementAsync.run({});
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;
    setPropertyOptions((resp.response.propertyOptions || []).map(i => _objectSpread(_objectSpread({}, i), {}, {
      isChosen: true
    })));
  };
  const isAllChosen = list => !list.some(i => !i.isChosen);
  const toggleAll = list => {
    const anyUnselected = list.some(i => !i.isChosen);
    return list.map(i => _objectSpread(_objectSpread({}, i), {}, {
      isChosen: anyUnselected
    }));
  };
  const handlePropertyChange = valueId => {
    setPropertyOptions(prev => valueId === 'select-all' ? toggleAll(prev) : prev.map(p => p.propertyId === valueId ? _objectSpread(_objectSpread({}, p), {}, {
      isChosen: !p.isChosen
    }) : p));
  };
  const chosenProperties = propertyOptions.filter(p => p.isChosen);
  const receiptPropertyId = chosenProperties.length === 1 ? chosenProperties[0].propertyId : null;
  const findReceiptPeriod = key => receiptPeriods.find(p => "".concat(p.year, "-").concat(p.month) === key);

  // The receipt endpoint is authoritative: `reimbursementAmount` is the payout owed to the property
  // for the period - the same Payout row Property Details → "Reimbursed <Month>" shows - plus the
  // supporting `paymentsCollected` / `withheldOrAdjusted` breakdown. Nothing is derived here; a
  // client-side derivation is what made this tab disagree with Property Details.
  const fetchReceipt = async (propertyId, period) => {
    const receiptResp = await store.report.getReimbursementReceiptAsync.run({
      propertyId,
      year: period.year,
      month: period.month
    });
    if (!(receiptResp !== null && receiptResp !== void 0 && receiptResp.isSuccessful) || !receiptResp.response) return null;
    return receiptResp.response;
  };
  useEffect(() => {
    if (!receiptPropertyId) {
      setReceipt(null);
      setReceiptPeriods([]);
      setReceiptPeriodKey(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setReceiptLoading(true);
      try {
        var _resp$response, _periods$;
        const resp = await store.report.getReimbursementReceiptPeriodsAsync.run({
          propertyId: receiptPropertyId,
          monthsBack: 12
        });
        const periods = resp !== null && resp !== void 0 && resp.isSuccessful ? (_resp$response = resp.response) !== null && _resp$response !== void 0 ? _resp$response : [] : [];
        if (cancelled) return;
        setReceiptPeriods(periods);
        const first = (_periods$ = periods[0]) !== null && _periods$ !== void 0 ? _periods$ : null;
        setReceiptPeriodKey(first ? "".concat(first.year, "-").concat(first.month) : null);
        const firstReceipt = first ? await fetchReceipt(receiptPropertyId, first) : null;
        if (!cancelled) setReceipt(firstReceipt);
      } catch (_unused) {
        if (!cancelled) {
          setReceipt(null);
          setReceiptPeriods([]);
          setReceiptPeriodKey(null);
        }
      } finally {
        if (!cancelled) setReceiptLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptPropertyId]);
  const handleReceiptPeriodChange = async key => {
    const period = findReceiptPeriod(key);
    setReceiptPeriodKey(key);
    if (!receiptPropertyId || !period) return;
    setReceiptLoading(true);
    try {
      setReceipt(await fetchReceipt(receiptPropertyId, period));
    } catch (_unused2) {
      setReceipt(null);
    } finally {
      setReceiptLoading(false);
    }
  };
  const handleExportReceipt = async () => {
    var _resp$headers$fileNa, _resp$headers;
    const period = findReceiptPeriod(receiptPeriodKey);
    if (!receiptPropertyId || !period) return;
    // run() resolves to undefined on HTTP error / cancellation - guard before destructuring.
    const resp = await store.report.downloadReimbursementReceiptAsync.run({
      propertyId: receiptPropertyId,
      year: period.year,
      month: period.month
    });
    if (!(resp !== null && resp !== void 0 && resp.data)) return;
    const fileName = (_resp$headers$fileNa = (_resp$headers = resp.headers) === null || _resp$headers === void 0 ? void 0 : _resp$headers['file-name']) !== null && _resp$headers$fileNa !== void 0 ? _resp$headers$fileNa : "reimbursement-receipt-".concat(period.year, "-").concat(period.month, ".csv");
    const url = window.URL.createObjectURL(new Blob([resp.data], {
      type: 'application/octet-stream'
    }));
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  useEffect(() => {
    setIsLoading(true);
    getFilters().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(WipBanner, null), /*#__PURE__*/React.createElement("div", {
    className: classes.header
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", {
    className: classes.title
  }, "Reimbursement Receipts"), /*#__PURE__*/React.createElement("p", {
    className: classes.subtitle
  }, "The payout Livable owes back to a property for a single property and period, with the resident payments collected through the software behind it - net of retained convenience fees, checks excluded.")), isLoading && /*#__PURE__*/React.createElement(CircularProgress, {
    size: 22,
    style: {
      color: COLORS.teal
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.filters
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.mdSelector
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "rrc-property",
    label: "Property",
    multiple: true,
    disabled: isLoading,
    value: propertyOptions.filter(i => i.isChosen).map(i => i.propertyName),
    renderValue: selected => isAllChosen(propertyOptions) ? 'All properties' : selected.join(', '),
    onChange: e => handlePropertyChange(e.target.value[e.target.value.length - 1])
  }, /*#__PURE__*/React.createElement(MenuItem, {
    key: "select-all",
    value: "select-all",
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: isAllChosen(propertyOptions)
  }), /*#__PURE__*/React.createElement(ListItemText, null, "All properties")), propertyOptions.map(i => /*#__PURE__*/React.createElement(MenuItem, {
    key: i.propertyId,
    value: i.propertyId,
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: i.isChosen
  }), /*#__PURE__*/React.createElement(ListItemText, null, i.propertyName)))))), /*#__PURE__*/React.createElement("div", {
    className: classes.grid
  }, /*#__PURE__*/React.createElement(Panel, {
    span: 12,
    title: "Reimbursement receipt",
    subtitle: "Payout owed to the property for a single property and period - the same figure as Property Details, net of amounts withheld for outstanding resident balances. The table below lists the resident payments collected.",
    accent: MEASURE.paid,
    right: /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        gap: 10,
        alignItems: 'center'
      }
    }, receiptPropertyId && receiptPeriods.length > 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        minWidth: 150
      }
    }, /*#__PURE__*/React.createElement(SelectMenu, {
      id: "rrc-receipt-period",
      label: "Receipt period",
      disabled: receiptLoading,
      value: (_findReceiptPeriod$pe = (_findReceiptPeriod = findReceiptPeriod(receiptPeriodKey)) === null || _findReceiptPeriod === void 0 ? void 0 : _findReceiptPeriod.period) !== null && _findReceiptPeriod$pe !== void 0 ? _findReceiptPeriod$pe : '',
      renderValue: () => {
        var _findReceiptPeriod$pe2, _findReceiptPeriod2;
        return (_findReceiptPeriod$pe2 = (_findReceiptPeriod2 = findReceiptPeriod(receiptPeriodKey)) === null || _findReceiptPeriod2 === void 0 ? void 0 : _findReceiptPeriod2.period) !== null && _findReceiptPeriod$pe2 !== void 0 ? _findReceiptPeriod$pe2 : '';
      },
      onChange: e => handleReceiptPeriodChange(e.target.value)
    }, receiptPeriods.map(p => /*#__PURE__*/React.createElement(MenuItem, {
      key: "".concat(p.year, "-").concat(p.month),
      value: "".concat(p.year, "-").concat(p.month)
    }, p.period)))), /*#__PURE__*/React.createElement(Button, {
      variant: "outlined",
      disabled: !receipt || receiptLoading,
      onClick: handleExportReceipt,
      style: {
        textTransform: 'none',
        borderColor: COLORS.border,
        color: COLORS.teal
      }
    }, "Export to CSV"))
  }, !receiptPropertyId ? /*#__PURE__*/React.createElement(EmptyState, {
    message: "Select a single property above to view its reimbursement receipt"
  }) : receipt && (_receipt$transactions = receipt.transactions) !== null && _receipt$transactions !== void 0 && _receipt$transactions.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.kpiRow,
    style: {
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement(KpiCard, {
    label: "Reimbursement amount",
    value: pickReceiptReimbursement(receipt).isPayout ? formatCurrency(pickReceiptReimbursement(receipt).value) : '-',
    tone: MEASURE.paid,
    footnote: pickReceiptReimbursement(receipt).isPayout ? 'Payout owed to the property - same figure as Property Details' : 'Payout not yet computed for this period'
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "Payments collected",
    value: formatCurrency(receipt.paymentsCollected),
    tone: COLORS.text,
    footnote: "".concat(receipt.transactions.length, " resident payment").concat(receipt.transactions.length === 1 ? '' : 's', " through Livable")
  }), pickReceiptWithheld(receipt) !== null && /*#__PURE__*/React.createElement(KpiCard, {
    label: "Withheld / adjustments",
    value: formatCurrency(pickReceiptWithheld(receipt)),
    tone: COLORS.textFaint,
    footnote: "Held against outstanding resident balances and ledger adjustments"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.tableWrap
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Type"), /*#__PURE__*/React.createElement("th", null, "Resident / Unit"), /*#__PURE__*/React.createElement("th", null, "Date"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Amount"))), /*#__PURE__*/React.createElement("tbody", null, receipt.transactions.map((t, i) => {
    const isRefund = t.type === 'Refund';
    const tone = isRefund ? MEASURE.unpaid : MEASURE.paid;
    return /*#__PURE__*/React.createElement("tr", {
      key: "".concat(t.unit, "-").concat(t.date, "-").concat(i)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      style: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        color: tone,
        background: "".concat(tone, "1A")
      }
    }, t.type)), /*#__PURE__*/React.createElement("td", null, t.residentName, t.unit ? /*#__PURE__*/React.createElement("span", {
      style: {
        color: COLORS.textFaint
      }
    }, " \xB7 Unit ", t.unit) : null), /*#__PURE__*/React.createElement("td", null, t.date), /*#__PURE__*/React.createElement("td", {
      className: classes.num,
      style: {
        color: tone,
        fontWeight: 600
      }
    }, formatCurrency(t.amount)));
  }))))) : /*#__PURE__*/React.createElement(EmptyState, {
    message: receiptLoading ? 'Loading…' : 'No payments collected through Livable for this period'
  })), /*#__PURE__*/React.createElement(Panel, {
    span: 12,
    title: "Period history (12 months)",
    subtitle: "Payout by period, with the payments collected behind it",
    accent: COLORS.slate
  }, receiptPropertyId && receiptPeriods.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.tableWrap
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Period"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Reimbursement amount"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Payments collected"))), /*#__PURE__*/React.createElement("tbody", null, receiptPeriods.map(p => /*#__PURE__*/React.createElement("tr", {
    key: "h-".concat(p.year, "-").concat(p.month)
  }, /*#__PURE__*/React.createElement("td", null, p.period), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      color: MEASURE.paid
    }
  }, p.payoutComputed ? formatCurrency(p.reimbursementAmount) : 'Not yet computed'), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      color: COLORS.textFaint
    }
  }, formatCurrency(p.paymentsCollected))))))) : /*#__PURE__*/React.createElement(EmptyState, {
    message: "Select a single property above to view its 12-month history"
  }))));
});